import React, { useContext } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { BaseFormCard, BaseTabs } from "../../components/base";

import ErrorSection from "../../components/error-section";
import Connectors from "../../components/manage-profile/connectors";

import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom";

import { FormattedMessage } from "react-intl";
import CompanyInfo from "../../components/manage-profile/company-info";
import UserInfo from "../../components/manage-profile/user-info";
import { IconButton, Tooltip } from "@mui/material";
import UserSettings from "../../components/manage-profile/user-settings";
import AccessKeys from "../../components/manage-profile/access-keys";
import DataFiles from "../../components/manage-profile/data-files";

import UserContext from "../../services/user/user";
import APIContext from "../../services/api/api";
import { EcAuthContext } from "@eclear-ag/ec-common-ui";
import Subscriptions from "../../components/manage-profile/subscriptions/subscriptions";
import SubscriptionDetailRoute from "../../components/manage-profile/subscriptions/subscription-detail-route";
import ChangePassword from "../../components/manage-profile/change-password/change-password";
import InfoIcon from "../../assets/icons/InfoIcon";

// index -> ?tab= name, one map per layout (default vs allowToSpecificDomain).
const DEFAULT_TAB_NAMES = {
  0: "financialConnectors",
  1: "userInformation",
  2: "companyInformation",
  3: "userSettings",
  4: "accessKeys",
  5: "dataFiles",
  6: "subscriptions"
};

const SPECIFIC_DOMAIN_TAB_NAMES = {
  0: "userInformation",
  1: "companyInformation",
  2: "userSettings",
  3: "subscriptions"
};

export default function ManageProfile() {
  const { state } = useLocation();
  const { productType } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDirectCustomer } = useContext(EcAuthContext);
  const { allowToSpecificDomain } = useContext(APIContext);
  const subscriptionsTabIndex = allowToSpecificDomain() ? 3 : 6;
  const tabNames = allowToSpecificDomain()
    ? SPECIFIC_DOMAIN_TAB_NAMES
    : DEFAULT_TAB_NAMES;

  const tabIndexForName = (name) => {
    const entry = Object.entries(tabNames).find(([, tabName]) => tabName === name);
    return entry ? Number(entry[0]) : 0;
  };

  const initialTabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = React.useState(
    productType
      ? subscriptionsTabIndex
      : initialTabParam
      ? tabIndexForName(initialTabParam)
      : state
      ? state.goToTab
      : 0
  );

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  const { isSpotEmpty } = useContext(APIContext);

  const {
    isConnectorSaveSuccess,
    setIsConnectorSaveSuccess,
    isUserSaveSuccess,
    setIsUserSaveSuccess,
    isCompanySaveSuccess,
    setIsCompanySaveSuccess,
    isSnackBarVisible,
    setIsSnackBarVisible,
    isSaveSuccess,
    setIsSaveSuccess,
    setIsAccessKeyErrorVisible,
    isAccessKeyErrorVisible,
    isWhiteLabel
  } = React.useContext(UserContext);

  const handleChangeIndex = (index) => {
    const nextTab = window.location.host.includes("jera") ? index + 1 : index;
    setActiveTab(nextTab);
    const tabName = tabNames[nextTab];
    if (!tabName) {
      return;
    }
    if (productType) {
      navigate(`/spot/manage-profile?tab=${tabName}`, { replace: true });
      return;
    }
    setSearchParams(
      (params) => {
        params.set("tab", tabName);
        return params;
      },
      { replace: true }
    );
  };

  return (
    <React.Fragment>
      {/* Page Title */}
      <Typography
        variant="h2"
        className="text"
        sx={{ fontWeight: 500, fontSize: "36px", mb: "24px", mt: 0 }}
      >
        <FormattedMessage id="welcomeToYourProfile" />
      </Typography>
      {/* Error Section */}
      {isSpotEmpty === true ||
      localStorage.getItem("isSpotEmpty") === "true" ? (
        <ErrorSection
          errorList={[
            {
              header: "No Data available yet!",
              list: [
                'Please add your Financial-Connectors  to your profile. <span to="/">Here</span>'
              ]
            }
          ]}
        >
          <Typography variant="h3" className="error-header">
            <FormattedMessage id="spotNoData" />
          </Typography>
          <ul>
            <li>
              <FormattedMessage
                id="financialConnectors.error"
                values={{
                  b: (...chunks) => {
                    return <b style={{ fontWeight: "500" }}>{chunks}</b>;
                  },
                  br: <br />
                }}
              />
            </li>
          </ul>
        </ErrorSection>
      ) : null}
      {/* Tabs section */}
      {!allowToSpecificDomain() && (
        <Box>
          <BaseTabs
            value={activeTab}
            onChange={(e, idx) => {
              handleChangeIndex(idx);
            }}
            aria-label="profile manager tabs"
            tabs={[
              !window.location.host.includes("jera") && {
                value: 0,
                label: <FormattedMessage id="financialConnectors" />
              },
              { value: 1, label: <FormattedMessage id="userInformation" /> },
              { value: 2, label: <FormattedMessage id="companyInformation" /> },
              { value: 3, label: <FormattedMessage id="userSettings" /> },
              !isDirectCustomer && {
                value: 4,
                label: <FormattedMessage id="accessKeys" />
              },
              !isDirectCustomer && {
                value: 5,
                label: <FormattedMessage id="dataFiles" />
              },
              { value: 6, label: <FormattedMessage id="subscriptions" /> }
            ].filter(Boolean)}
          />
          <Box sx={{ marginTop: "20px" }}>
            {!window.location.host.includes("jera") && (
              <TabPanel
                id="full-width-tabpanel-0"
                value={activeTab}
                index={0}
                sx={{ pt: 3 }}
              >
                <Connectors />
              </TabPanel>
            )}
            <TabPanel id="full-width-tabpanel-1" value={activeTab} index={1}>
              <UserInfo showSave={true} />
            </TabPanel>
            <TabPanel id="full-width-tabpanel-2" value={activeTab} index={2}>
              <CompanyInfo />
            </TabPanel>
            <TabPanel id="full-width-tabpanel-3" value={activeTab} index={3}>
              <UserSettings />
            </TabPanel>
            <TabPanel id="full-width-tabpanel-4" value={activeTab} index={4}>
              <Box
                className="card-main-plain"
                sx={{
                  p: "20px"
                }}
              >
                <AccessKeys />
              </Box>
            </TabPanel>
            <TabPanel id="full-width-tabpanel-5" value={activeTab} index={5}>
              <Box
                className="card-main-plain"
                sx={{
                  p: "20px"
                }}
              >
                <DataFiles />
              </Box>
            </TabPanel>
            <TabPanel id="full-width-tabpanel-6" value={activeTab} index={6}>
              <Box
                className="card-main-plain"
                sx={{
                  p: "0px"
                }}
              >
                {productType ? <SubscriptionDetailRoute /> : <Subscriptions />}
              </Box>
            </TabPanel>
          </Box>
        </Box>
      )}

      {allowToSpecificDomain() && (
        <Box>
          <BaseTabs
            value={activeTab}
            onChange={(e, idx) => {
              handleChangeIndex(idx);
            }}
            aria-label="profile manager tabs"
            tabs={[
              {
                value: 0,
                testId: "user-info-tab",
                label: <FormattedMessage id="userInformation" />
              },
              {
                value: 1,
                testId: "comany-info-tab",
                label: <FormattedMessage id="companyInformation" />
              },
              {
                value: 2,
                testId: "general-settings-tab",
                label: <FormattedMessage id="userSettings" />
              },
              {
                value: 3,
                testId: "subscripition-tab",
                label: <FormattedMessage id="subscriptions" />
              }
            ]}
          />
          <Box sx={{ marginTop: "20px" }}>
            <TabPanel id="full-width-tabpanel-0" value={activeTab} index={0}>
              <React.Fragment>
                <UserInfo showSave={true} />
                <BaseFormCard
                  title="password"
                  sx={{ mt: "20px" }}
                >
                  <Box sx={{ p: "16px" }}>
                    <ChangePassword />
                  </Box>
                </BaseFormCard>
              </React.Fragment>
            </TabPanel>
            <TabPanel id="full-width-tabpanel-1" value={activeTab} index={1}>
              <CompanyInfo showSave={true} />
            </TabPanel>
            <TabPanel id="full-width-tabpanel-2" value={activeTab} index={2}>
              <UserSettings />
            </TabPanel>
            <TabPanel id="full-width-tabpanel-3" value={activeTab} index={3}>
              <Box
                sx={{
                  p: "0px"
                }}
              >
                {productType ? <SubscriptionDetailRoute /> : <Subscriptions />}
              </Box>
            </TabPanel>
          </Box>
        </Box>
      )}
      <Snackbar
        open={isSnackBarVisible}
        autoHideDuration={6000}
        onClose={() => setIsSnackBarVisible(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
      >
        <Alert
          severity={isSaveSuccess ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {isSaveSuccess ? (
            <FormattedMessage id="kpi.success" />
          ) : (
            <FormattedMessage id="kpi.error" />
          )}
        </Alert>
      </Snackbar>
      <Snackbar
        open={isAccessKeyErrorVisible}
        autoHideDuration={6000}
        onClose={() => setIsAccessKeyErrorVisible(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
      >
        <Alert severity={"error"} sx={{ width: "100%" }}>
          <FormattedMessage id="accessKeyError" />
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

// helpers Tab Panel

function TabPanel(props) {
  const { sx, children, value, index, ...other } = props;

  // Render only the active panel — no sliding animation between tabs.
  if (Number(value) !== Number(index)) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      <Box sx={sx}>{children}</Box>
    </div>
  );
}
